import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import MonitoringCategoryCard from './Card/MonitoringCategoryCard';
import MonitoringTopicCard from './Card/MonitoringTopicCard';

interface Topic {
	id: string;
	title: string;
}

interface Category {
	id: string;
	title: string;
	topics: Topic[];
}

export default function PhaseCategories({ categories, isLoading }: any) {
	const { t } = useTranslation();
	const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
	const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		if (categories && categories.length > 0) {
			// Check URL for category ID
			const searchParams = new URLSearchParams(location.search);
			const categoryId = searchParams.get('categoryId');

			// If category ID exists in URL, select that category
			if (categoryId) {
				const foundCategory = categories.find((cat: Category) => cat.id === categoryId);
				if (foundCategory) {
					setSelectedCategory(foundCategory);
					localStorage.setItem('category_id', foundCategory.id);
				} else {
					// If category ID in URL is invalid, use first category
					setSelectedCategory(categories[0]);
					localStorage.setItem('category_id', categories[0]?.id);
					updateUrlWithCategory(categories[0]?.id);
				}
			} else {
				// If no category ID in URL, use first category
				setSelectedCategory(categories[0]);
				localStorage.setItem('category_id', categories[0]?.id);
				updateUrlWithCategory(categories[0]?.id);
			}

			// Initialize all categories as expanded
			const initialExpanded = categories.reduce((acc: any, cat: Category) => {
				acc[cat.id] = true;
				return acc;
			}, {});
			setExpandedCategories(initialExpanded);
		}
	}, [categories, location.search]);

	const updateUrlWithCategory = (categoryId: string | undefined) => {
		if (!categoryId) return;

		const searchParams = new URLSearchParams(location.search);
		searchParams.set('categoryId', categoryId);
		navigate({ search: searchParams.toString() }, { replace: true });
	};

	const handleSelectCategory = (category: Category) => {
		if (selectedCategory?.id === category.id) {
			// If clicking the same category, deselect it
			setSelectedCategory(null);
			localStorage.removeItem('category_id');

			// Remove category ID from URL
			const searchParams = new URLSearchParams(location.search);
			searchParams.delete('categoryId');
			navigate({ search: searchParams.toString() }, { replace: true });
		} else {
			// If clicking a different category, select it
			setSelectedCategory(category);
			localStorage.setItem('category_id', category.id);

			// Update URL with category ID
			updateUrlWithCategory(category.id);
		}
	};

	const toggleCategory = (categoryId: string, event: React.MouseEvent) => {
		event.stopPropagation();
		setExpandedCategories((prev) => ({
			...prev,
			[categoryId]: !prev[categoryId],
		}));
	};

	if (isLoading) {
		return (
			<div className="flex flex-col md:flex-row gap-5">
				<div className="flex flex-col gap-2 w-full md:w-[18%]">
					<span className="text-base font-normal text-dark">{t('checkUpCategories')}</span>
					<div className="flex flex-col gap-3">
						{[...Array(5)].map((_, index) => (
							<div key={index} className="h-16 bg-gray-300 rounded animate-pulse"></div>
						))}
					</div>
				</div>
				<div className="flex flex-row bg-[#F5EFE9] rounded-lg p-5 w-full h-screen">
					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-5">
						{[...Array(8)].map((_, index) => (
							<div key={index} className="h-16 bg-gray-300 rounded animate-pulse"></div>
						))}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col md:flex-row gap-5">
			<div className="relative w-full md:w-[18%]">
				<span className="text-base font-normal text-dark mb-2 block">{t('checkUpCategories')}</span>
				<div className="flex flex-col gap-3">
					{categories?.map((category: Category) => (
						<div key={category.id} className="relative">
							<div className="flex items-center gap-2">
								<div
									className="flex-grow cursor-pointer"
									onClick={() => handleSelectCategory(category)}
								>
									<MonitoringCategoryCard
										selected={selectedCategory?.id === category.id}
										category={category}
									/>
								</div>
								<button
									className="md:hidden absolute right-2 top-1/2 transform -translate-y-1/2 p-2"
									onClick={(e) => toggleCategory(category.id, e)}
								>
									<svg
										className={`w-6 h-6 transform transition-transform ${
											expandedCategories[category.id] ? 'rotate-180' : ''
										}`}
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M19 9l-7 7-7-7"
										/>
									</svg>
								</button>
							</div>
							<div
								className={`md:hidden transition-all duration-300 overflow-scroll ${
									expandedCategories[category.id] && selectedCategory?.id === category.id
										? 'max-h-[500px] opacity-100 mt-3'
										: 'max-h-0 opacity-0'
								}`}
							>
								{selectedCategory?.id === category.id && (
									<div className="grid grid-cols-1 gap-3 bg-[#F5EFE9] rounded-lg p-3">
										{category.topics.map((topic) => (
											<MonitoringTopicCard key={topic.id} data={topic} />
										))}
									</div>
								)}
							</div>
						</div>
					))}
				</div>
			</div>
			<div className="hidden md:flex flex-row bg-[#F5EFE9] rounded-lg p-5 w-full min-h-[50vh] md:h-screen">
				<div className="grid grid-cols-1 sm:grid-cols-2 h-[160px] md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-5 w-full">
					{selectedCategory?.topics.map((topic) => (
						<MonitoringTopicCard key={topic.id} data={topic} />
					))}
				</div>
			</div>
		</div>
	);
}
